import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { useLocation } from "react-router-dom";

import { ModuleSettingsMenu } from "@/components/layout/ModuleSettingsMenu";
import { ModuleSwitcher } from "@/components/layout/ModuleSwitcher";
import { RecordScopePicker } from "@/components/layout/RecordScopePicker";
import { RecordScopeSection } from "@/components/layout/RecordScopeSection";
import { UserMenu } from "@/components/layout/UserMenu";
import {
  NavGroup,
  NavLeaf,
  SidebarBrandLink,
  SidebarFullLogo,
} from "@/components/layout/nav-primitives";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import {
  getModuleNavSections,
  moduleSettingsToggles,
  SETTINGS_SECTION_SUFFIX,
  type ModuleDefinition,
  type ModuleSection,
  type RecordScope,
} from "@/lib/module-registry";
import type { AppRoute } from "@/lib/routes";
import { useAuthSession } from "@/lib/session-context";
import { useSidebarLayout } from "@/lib/sidebar-layout";
import { cn } from "@/lib/utils";

interface ModuleSidebarProps {
  /** Which module's nav to render. */
  mod: ModuleDefinition;
  /** True when the off-canvas sidebar is open on narrow viewports. */
  mobileOpen: boolean;
  /** Closes the mobile drawer — called on backdrop click or nav tap. */
  onClose: () => void;
}

/**
 * The sidebar, for every module.
 *
 * Replaces `Sidebar` (Sales), `GlobalSidebar`, and `ReportsSidebar` — three
 * ~165-line files that were character-identical apart from four lines each:
 * which nav-sections function they called, which module supplied the compact
 * brand mark, and the `activeKey` they handed `ModuleSwitcher`. All three of
 * those are now properties of the module definition, so the fork collapses to
 * one component with one prop.
 *
 * Row rendering was already shared (`nav-primitives.tsx` takes bare
 * `AppRoute`s and needed no changes), as was the compact/expanded toggle
 * (`useSidebarLayout`) and the footer (`UserMenu`).
 */
export function ModuleSidebar({ mod, mobileOpen, onClose }: ModuleSidebarProps) {
  const session = useAuthSession();
  const { pathname, search } = useLocation();
  const allSectionsUnfiltered = getModuleNavSections(
    mod,
    session.is_super_admin,
    session.features,
    session.permissions,
    session.is_supplier,
  );
  // `visibleWhen` is keyed on the section, but `getModuleNavSections` returns
  // plain `{ section, label, items }` — look the predicate back up by key
  // rather than threading it through the flattening.
  const sectionByKey = new Map(mod.sections.map((s) => [s.key, s]));
  const allSections = allSectionsUnfiltered.filter((s) => {
    const visibleWhen = sectionByKey.get(s.section)?.visibleWhen;
    return !visibleWhen || visibleWhen({ pathname, search });
  });

  // A module's own settings move out of the nav list and into the footer gear
  // (`ModuleSettingsMenu`) — occasional configuration shouldn't sit at the
  // same visual weight as the daily work, and pinning it to the footer gives
  // it one position that's identical in every module.
  //
  // Keyed on the `SETTINGS_SECTION_SUFFIX` convention rather than a flag on
  // `ModuleSection`: the settings group is a *rendering* decision the sidebar
  // owns, and a module shouldn't have to opt in to get the shared treatment.
  const settingsSections = allSections.filter((s) =>
    s.section.endsWith(SETTINGS_SECTION_SUFFIX),
  );
  const sections = allSections.filter(
    (s) => !s.section.endsWith(SETTINGS_SECTION_SUFFIX),
  );

  // Flatten a level: the section holds one parent group ("Settings") whose
  // `subItems` are the real pages. The gear IS that parent, so its children
  // become the menu's rows. A settings section declaring plain routes rather
  // than a group works too — those pass through as-is.
  const settingsItems = settingsSections.flatMap((s) =>
    s.items.flatMap((item) =>
      item.subItems && item.subItems.length > 0 ? item.subItems : [item],
    ),
  );
  // Switches that sit in the gear's popover itself. Super-admin only, like
  // every page behind the gear.
  const settingsToggles = moduleSettingsToggles(mod, session.is_super_admin);
  const hasSettings = settingsItems.length > 0 || settingsToggles.length > 0;

  // Record-scoped sections resolve in their own child component
  // (`RecordScopeSection`) rather than here. Their selection comes from a
  // hook, and this component renders whichever module is active — so calling
  // `useSelection` in a loop over `mod.sections` would change the hook count
  // when the user switches modules, which is a Rules-of-Hooks violation, not
  // a theoretical one. One component per section keeps each hook call in a
  // fixed position within its own render.
  //
  // The predicate narrows the element type, so `RecordScopeSection` /
  // `RecordScopePicker` receive a section whose `recordScope` is non-optional
  // — which is what lets them call `useScope()` unconditionally.
  const scopedSections = mod.sections
    .filter((s) => !s.visibleWhen || s.visibleWhen({ pathname, search }))
    .filter(
      (s): s is ModuleSection & { recordScope: RecordScope } => !!s.recordScope,
    );
  const { mode, toggle } = useSidebarLayout();
  // Sidebar mode is a desktop concern: below lg the off-canvas drawer covers
  // the left side regardless, so collapsing to icons inside it wastes space.
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const compact = isDesktop && mode === "icons";

  return (
    <>
      {/* Backdrop — mobile only. Click closes the drawer. */}
      <div
        className={cn(
          "fixed inset-0 z-30 bg-black/40 transition-opacity lg:hidden",
          mobileOpen ? "opacity-100" : "pointer-events-none opacity-0",
        )}
        onClick={onClose}
        aria-hidden="true"
      />

      <aside
        className={cn(
          // Below the "Viewing as" bar while it shows (`top-viewing-as`),
          // and the rest of the window's height.
          "fixed bottom-0 left-0 top-viewing-as z-40 flex w-[280px] flex-col border-r border-border bg-background-deep transition-[width,transform]",
          "lg:sticky lg:top-viewing-as lg:z-40 lg:h-[calc(100vh-var(--viewing-as-bar-height))] lg:translate-x-0",
          mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0",
          compact ? "lg:w-[64px]" : "lg:w-[280px]",
        )}
        style={{
          transition:
            "transform 200ms ease, width 200ms ease, background var(--duration-theme), border-color var(--duration-theme)",
        }}
        data-sidebar-mode={mode}
      >
        {/* Collapse/expand pill — hangs on the right edge, centered on the
            topbar's bottom-border seam so it reads as the hinge joining the
            two surfaces. Desktop only.

            `z-10` keeps the whole pill clickable. Without it the logo block
            below — positioned, and later in the DOM — painted over the pill's
            inner half, so a click there landed on the logo's dashboard link
            instead of collapsing the sidebar. */}
        <button
          type="button"
          onClick={toggle}
          aria-label={compact ? "Expand sidebar" : "Collapse sidebar"}
          className="absolute -right-3 top-14 z-10 hidden h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full border border-border bg-background-deep text-muted-foreground shadow-sm transition-colors hover:text-foreground lg:flex"
        >
          {compact ? (
            <ChevronRight size={13} strokeWidth={2} />
          ) : (
            <ChevronLeft size={13} strokeWidth={2} />
          )}
        </button>

        {compact ? (
          <SidebarBrandLink
            to="/dashboard"
            compact
            appLabel={mod.label}
            appIcon={mod.icon}
          />
        ) : (
          <div className="relative">
            <SidebarFullLogo to="/dashboard" />
            <button
              type="button"
              onClick={onClose}
              aria-label="Close navigation"
              className="absolute right-2 top-5 flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-row-hover hover:text-foreground lg:hidden"
            >
              <X size={16} />
            </button>
            <div className="px-4 pb-4 pt-3">
              <ModuleSwitcher activeKey={mod.key} />
              {/* A module's own header content — e.g. Market Intelligence's
                  Power/Gas toggle — sits directly under the switcher, above
                  any record scope picker (module › mode › record). */}
              {mod.sidebarHeader && (
                <div className="mt-2">{mod.sidebarHeader()}</div>
              )}
              {/* The record picker sits under the module switcher — module ›
                  record, one scope down — for any module whose pages are
                  record-scoped. It stays put on every page, including shared
                  records, because it IS the module's scope rather than a page
                  inside it.

                  `mt-2` keeps it visually attached to the switcher above
                  (module › client is one scope chain) while its own accent
                  fill and stripe carry the distinction. It sits at the header
                  block's own inset rather than pulled out to the nav rows'
                  edge — as a control it belongs to the header, not to the row
                  list. */}
              {scopedSections.map((section) => (
                <div key={section.key} className="mt-2">
                  <RecordScopePicker section={section} onClose={onClose} />
                </div>
              ))}
            </div>
          </div>
        )}

        <nav
          className={cn(
            "scrollbar-thin flex-1 overflow-y-auto pb-3",
            compact ? "flex flex-col items-center px-2" : "px-3",
          )}
        >
          {sections.map((section, sIdx) => (
            <div
              key={section.section}
              className={cn(
                "flex w-full flex-col",
                compact ? "items-center gap-1" : "mb-2",
                // In icons mode there's no room for a section label, so a
                // hairline rule does the grouping work instead.
                compact && sIdx > 0 && "mt-2 border-t border-border pt-2",
              )}
            >
              {!compact && section.label && (
                <div className="mb-1 px-2 pt-2 text-xs font-bold uppercase tracking-eyebrow text-text-dim">
                  {section.label}
                </div>
              )}
              <SectionRows
                items={section.items}
                useRows={sectionByKey.get(section.section)?.useRows}
                compact={compact}
                onClose={onClose}
              />
            </div>
          ))}

          {/* Record-scoped sections come last and render indented — they're a
              narrowing of the module's scope, nested under the record-list
              row that leads into them (Clients › Hines › Hines's tools).
              Each drops itself when nothing is selected. */}
          {scopedSections.map((section) => (
            <RecordScopeSection
              key={section.key}
              section={section}
              compact={compact}
              onClose={onClose}
            />
          ))}
        </nav>

        {/* The gear gets its OWN bordered region above the account box, not a
            row inside it. Sharing one container made it read as part of the
            user widget — same fill, same padding, one enclosing border — when
            it's module configuration and the box below is your identity.
            Separate regions, each with its own top rule, keep the two
            scopes visually distinct.

            `ModuleSettingsMenu` returns null when the module declares no
            settings (or auth filters them all out), so this whole strip
            collapses and the footer is exactly the account box — which is
            why the border lives here rather than on the shared parent. */}
        {hasSettings && (
          <div
            className={cn(
              "border-t border-border",
              compact
                ? "flex flex-col items-center px-2 py-2"
                : "flex flex-col px-3 py-2",
            )}
          >
            <ModuleSettingsMenu
              moduleLabel={mod.label}
              items={settingsItems}
              toggles={settingsToggles}
              compact={compact}
              onClose={onClose}
            />
          </div>
        )}

        <div
          className={cn(
            "border-t border-border",
            compact ? "flex flex-col items-center px-2 py-3" : "px-3 py-3",
          )}
        >
          <UserMenu compact={compact} />
        </div>
      </aside>
    </>
  );
}

/**
 * One section's rows, with its dynamic children resolved.
 *
 * **Its own component because `useRows` is a hook.** `ModuleSidebar` renders
 * whichever module is active and loops over that module's sections, so calling
 * a per-section hook inline would change the hook count the moment the user
 * switched modules — a Rules-of-Hooks violation that unmounts the sidebar.
 * One component per section pins the call to a fixed position in its own
 * render tree. Same reasoning `RecordScopeSection` documents for `useScope`.
 *
 * Sections without `useRows` still render through here and simply take the
 * declared items, so there is one row-rendering path rather than two that can
 * drift.
 */
function SectionRows({
  items,
  useRows,
  compact,
  onClose,
}: {
  items: AppRoute[];
  useRows?: ModuleSection["useRows"];
  compact: boolean;
  onClose: () => void;
}) {
  // Called unconditionally — the `?? noRows` keeps the hook count fixed
  // whether or not this section declares one, which is what lets a single
  // component serve both kinds.
  const rowsFor = (useRows ?? noRows)();

  return (
    <>
      {items.map((item) => {
        // `undefined` leaves the declared `subItems` alone, so a static
        // section is untouched and a dynamic one falls back to its first-paint
        // list until the data lands.
        const subItems = rowsFor(item.path) ?? item.subItems;
        const resolved = subItems === item.subItems ? item : { ...item, subItems };

        return subItems && subItems.length > 0 ? (
          <NavGroup
            key={item.path}
            parent={resolved}
            onClose={onClose}
            compact={compact}
          />
        ) : (
          <NavLeaf
            key={item.path}
            item={resolved}
            onClose={onClose}
            compact={compact}
          />
        );
      })}
    </>
  );
}

/** The no-op `useRows`: a hook, so the call above is unconditional. */
function noRows(): (path: string) => AppRoute[] | undefined {
  return () => undefined;
}
